export const calculateSummary = (shows, generalExpenses) => {
  const summary = {
    totalGuarantee: 0,
    totalCashMerch: 0,
    totalVenmoMerch: 0,
    totalMerch: 0,
    totalShowExpenses: 0,
    totalGeneralExpenses: 0,
    totalExpenses: 0,
    grossTotal: 0,
    netProfit: 0,
    totalIncome: 0,
    byDay: []
  };

  const sortedDates = Object.keys(shows).sort((a, b) => {
    const [yearA, monthA, dayA] = a.split('-').map(Number);
    const [yearB, monthB, dayB] = b.split('-').map(Number);
    const dateA = new Date(yearA, monthA - 1, dayA);
    const dateB = new Date(yearB, monthB - 1, dayB);
    return dateA - dateB;
  });

  sortedDates.forEach(dateKey => {
    const dayShows = shows[dateKey];
    const [year, month, day] = dateKey.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    let dayGuarantee = 0;
    let dayCashMerch = 0;
    let dayVenmoMerch = 0;
    let dayExpenses = 0;

    // Filter out "other" and "personal" event types - only include shows/gigs
    const gigShows = dayShows.filter(show => {
      const eventType = show.eventType || 'show';
      return eventType !== 'other' && eventType !== 'personal';
    });

    // Skip this day if no gig shows
    if (gigShows.length === 0) return;

    // Map shows with all their data and add dateKey
    const showsWithDetails = gigShows.map(show => {
      // DEBUG: Log show data being processed
      console.log('📊 [calculateSummary] Processing show:', {
        dateKey,
        showId: show.id,
        title: show.title || show.venue,
        cashMerch: show.cashMerch,
        merchSales: show.merchSales,
        merchSalesLength: show.merchSales?.length || 0,
        merchSalesType: typeof show.merchSales,
        isArray: Array.isArray(show.merchSales),
        allShowKeys: Object.keys(show)
      });

      const guarantee = parseFloat(show.guarantee) || 0;
      const cashMerch = parseFloat(show.cashMerch) || 0;
      const venmoMerch = parseFloat(show.venmoMerch) || 0;
      const gas = parseFloat(show.gas) || 0;
      const hotel = parseFloat(show.hotel) || 0;
      const food = parseFloat(show.food) || 0;
      const tolls = parseFloat(show.tolls) || 0;
      const other = parseFloat(show.otherExpenses) || 0;

      const income = guarantee + cashMerch + venmoMerch;
      const expenses = gas + hotel + food + tolls + other;
      const profit = income - expenses;

      dayGuarantee += guarantee;
      dayCashMerch += cashMerch;
      dayVenmoMerch += venmoMerch;
      dayExpenses += expenses;

      // Return show with ALL original data plus calculated fields and dateKey
      // Explicitly include merchSales array for itemized breakdown
      const result = {
        ...show, // Keep ALL original show data (title, venue, city, times, notes, merchSales, etc.)
        dateKey: dateKey, // ADD dateKey for updates
        date: dateKey, // ADD date for display
        income, // Calculated income for this show
        expenses, // Calculated expenses for this show
        profit, // Calculated profit for this show
        // Explicitly preserve these fields to ensure they're not lost
        merchSales: show.merchSales || [],
        cashMerch: show.cashMerch || '0',
        venmoMerch: show.venmoMerch || '0'
      };

      // DEBUG: Log the result
      console.log('📊 [calculateSummary] Result show:', {
        showId: result.id,
        merchSalesInResult: result.merchSales,
        merchSalesLengthInResult: result.merchSales?.length || 0
      });

      return result;
    });

    const dayMerchTotal = dayCashMerch + dayVenmoMerch;
    const dayGross = dayGuarantee + dayMerchTotal;
    const dayNet = dayGross - dayExpenses;

    if (dayGross > 0 || showsWithDetails.length > 0) {
      summary.byDay.push({
        date: date,
        dateKey: dateKey,
        shows: showsWithDetails, // Use mapped shows with dateKey and all original data
        guarantee: dayGuarantee,
        cashMerch: dayCashMerch,
        venmoMerch: dayVenmoMerch,
        merchTotal: dayMerchTotal,
        expenses: dayExpenses,
        gross: dayGross,
        net: dayNet,
        dayIncome: dayGross, // Added for compatibility
        dayExpenses: dayExpenses,
        dayProfit: dayNet
      });
    }

    summary.totalGuarantee += dayGuarantee;
    summary.totalCashMerch += dayCashMerch;
    summary.totalVenmoMerch += dayVenmoMerch;
    summary.totalShowExpenses += dayExpenses;
  });

  summary.totalGeneralExpenses = generalExpenses.reduce((sum, exp) => {
    return sum + (parseFloat(exp.amount) || 0);
  }, 0);

  summary.totalMerch = summary.totalCashMerch + summary.totalVenmoMerch;
  summary.grossTotal = summary.totalGuarantee + summary.totalMerch;
  summary.totalIncome = summary.grossTotal; // Added for compatibility
  summary.totalExpenses = summary.totalShowExpenses + summary.totalGeneralExpenses;
  summary.netProfit = summary.grossTotal - summary.totalExpenses;

  return summary;
};

export const getAllShows = (shows) => {
  const allShows = [];
  Object.keys(shows).forEach(dateKey => {
    const [year, month, day] = dateKey.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    shows[dateKey].forEach(show => {
      allShows.push({
        ...show,
        date: date,
        dateKey: dateKey
      });
    });
  });
  
  return allShows.sort((a, b) => a.date - b.date);
};